"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { Stethoscope, Cpu, Vote, Heart, ArrowRight, Activity, Microscope, ShieldCheck } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";
import { Badge } from "@/components/ui/badge";

const ACCENT: Record<string, { text: string; border: string; from: string }> = {
  red: { text: "text-red", border: "border-red/20", from: "from-red/15" },
  purple: { text: "text-purple", border: "border-purple/20", from: "from-purple/15" },
  cyan: { text: "text-cyan", border: "border-cyan/20", from: "from-cyan/15" },
};

const THREATS = [
  {
    title: "Medicine",
    label: "The Method",
    headline: "Clinical rigor, exported to the boardroom.",
    description:
      "Clinical training instills a discipline that business schools do not teach: evidence over intuition, protocol under pressure, triage when everything is urgent, and accountability not for effort but for outcome. A physician who has managed life-and-death decisions brings a precision to organizational leadership that no MBA can replicate.",
    icon: Stethoscope,
    iconBg: "bg-gradient-sovereign",
    href: "/speaking/leadership-as-diagnosis/",
    keywords: ["Evidence", "Protocol", "Triage", "Outcome accountability"],
    accent: "red",
  },
  {
    title: "Technology",
    label: "The Scale",
    headline: "Real-time intelligence, decisions in under an hour.",
    description:
      "Operations command a real-time intelligence fabric that processes millions of data points, monitors 200+ digital surfaces, predicts crises before they materialize, and orchestrates operations across continents. The infrastructure is the engine; the operator ensures the engine delivers.",
    icon: Cpu,
    iconBg: "bg-gradient-royal",
    href: "/expertise/strategic-intelligence/",
    keywords: ["Real-time", "Predictive", "Integrated", "Sovereign-grade"],
    accent: "purple",
  },
  {
    title: "Politics",
    label: "The Stakes",
    headline: "Sovereign clients, national consequences.",
    description:
      "When the client is a sovereign government, a royal household, a defense ministry, or a political leader facing an existential contest, the work is not theoretical. Outcomes shape nations, economies, public sentiment, and the lives of millions. Operating at this level demands not just competence but composure, discretion, and an unflinching commitment to results.",
    icon: Vote,
    iconBg: "bg-gradient-clarity",
    href: "/expertise/political-strategy/",
    keywords: ["Sovereign", "Discretion", "Composure", "Outcomes"],
    accent: "cyan",
  },
];

export function TripleThreatSection() {
  return (
    <Section id="triple-threat" ariaLabel="The Triple Threat" background="default">
      <SectionHeader
        icon={Microscope}
        overline="The Value Proposition"
        overlineClassName="text-amber-gold"
        pillClassName="glass-tinted-amber-gold border-amber-gold/20"
        title={
          <>
            Medicine. Technology. Politics. <br />
            <span className="text-gradient-royal">One Operator.</span>
          </>
        }
        subtitle="Most leaders specialize in a single domain. Few operate across three — and the intersection is where the value is unmatched."
      />

      <div className="grid md:grid-cols-3 gap-5 lg:gap-6 mb-12">
        {THREATS.map((t, i) => {
          const c = ACCENT[t.accent];
          return (
            <motion.div
              key={t.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.12, ease: [0, 0, 0.2, 1] }}
              className="group relative"
            >
              <Link href={t.href} className="block h-full">
                <div
                  className={`relative glass-card rounded-2xl p-7 h-full bg-gradient-to-br ${c.from} to-transparent overflow-hidden border ${c.border}`}
                >
                  <div aria-hidden className="absolute -top-4 -right-4 text-[8rem] font-bold leading-none opacity-[0.04] select-none">
                    0{i + 1}
                  </div>
                  <div className="relative mb-5">
                    <div className={`size-16 rounded-2xl ${t.iconBg} flex items-center justify-center shadow-2xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                      <t.icon className="size-8 text-white" />
                    </div>
                    <div aria-hidden className={`absolute inset-0 size-16 rounded-2xl ${t.iconBg} opacity-30 blur-xl group-hover:opacity-60 transition-opacity`} />
                  </div>
                  <div className="relative">
                    <div className={`text-overline ${c.text}`}>{t.title}</div>
                    <div className="text-caption text-muted-foreground mb-3 mt-1">— {t.label}</div>
                    <h3 className="text-heading-lg mb-3 leading-tight">{t.headline}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed mb-5">
                      {t.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mb-5">
                      {t.keywords.map((k) => (
                        <Badge key={k} variant="outline" className="text-caption font-normal">
                          {k}
                        </Badge>
                      ))}
                    </div>
                    <div className={`inline-flex items-center gap-1.5 text-body-sm font-semibold ${c.text} group-hover:gap-2.5 transition-all`}>
                      Explore {t.title}
                      <ArrowRight className="size-4" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>

      <div className="glass-strong rounded-2xl p-8 md:p-10 max-w-4xl mx-auto text-center gradient-border">
        <Heart className="size-8 text-red mx-auto mb-4 animate-fade-in" fill="currentColor" />
        <p className="text-body-lg text-foreground leading-relaxed">
          The triple threat is not three separate capabilities. It is{" "}
          <span className="text-gradient-sovereign font-semibold">one integrated operating method</span> —
          clinical rigor applied to technological systems under political-grade stakes. No other operator at
          this scale brings a physician&apos;s discipline to sovereign-level execution.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { cls: "text-lime", metric: "15+", label: "Years" },
          { cls: "text-indigo", metric: "60+", label: "Services" },
          { cls: "text-denim", metric: "18", label: "Countries" },
          { cls: "text-emerald", metric: "0", label: "Breaches" },
          { cls: "text-amber-gold", metric: "70%", label: "Women" },
          { cls: "text-coral", metric: "1", label: "Operator" },
        ].map((s) => (
          <div key={s.label} className="glass rounded-xl p-4 text-center">
            <div className={`text-display-sm ${s.cls} font-bold`}>{s.metric}</div>
            <div className="text-caption text-muted-foreground mt-1">{s.label}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}
